import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDeleteObsReport } from "@/features/obs/hooks/useObsQueries";
import { handleApiError } from "@/lib/error-handler";
import type { ObsReport } from "@/types/obs";

/**
 * Delete a created report, permanently.
 *
 * 🔴 THIS IS NOT UNPUBLISHING. Unpublishing takes a report off the landing page
 * and is reversible; this destroys the report AND every dataset version ever
 * published under it — the record of which figures the ministry published and
 * when. So the operator is told how many versions will go and has to type the
 * slug, which is the cheapest guard that requires actual intent rather than a
 * mis-click. The server demands the same confirmation independently.
 *
 * Offered for every report that has an identity row, the six included. Deleting
 * one of those is not quite the same act: its parsing schema lives in the code,
 * so the report can be recreated with `seed-obs-reports.js` and a re-upload —
 * what does not come back is the published versions, which is the part that
 * matters. The extra panel below says so before the slug is typed.
 */
export function ObsDeleteReportDialog({
  report,
  versions,
  open,
  onOpenChange,
}: {
  report: ObsReport;
  /** How many published versions go with it. */
  versions: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [typed, setTyped] = useState("");
  const remove = useDeleteObsReport();

  useEffect(() => {
    if (!open) return;
    setTyped("");
    remove.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirmed = typed.trim() === report.slug;

  const run = async () => {
    try {
      await remove.mutateAsync({ slug: report.slug });
      onOpenChange(false);
    } catch (e) {
      handleApiError(e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>حذف التقرير — {report.title.ar}</DialogTitle>
          <DialogDescription>
            إجراء نهائي لا يمكن التراجع عنه.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-sm">
            سيُحذف التقرير و<span className="font-semibold"> {versions} </span>
            إصدارًا منشورًا من بياناته. هذا سجلّ ما نُشر ومتى، ولا يمكن
            استرجاعه.
            <span className="mt-2 block text-xs text-muted-foreground">
              إن كان المقصود إخفاء التقرير من الموقع فقط، فألغِ نشره بدلًا من
              حذفه — وهذا إجراء قابل للتراجع.
            </span>
          </div>

          {report.isDeclared && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
              <span className="font-semibold">
                هذا أحد التقارير الستة الأصلية.
              </span>{" "}
              ستختفي شاشته من الموقع فور الحذف. تعريف قراءة الملف محفوظ في
              الكود، لذا يمكن إرجاعه بتشغيل{" "}
              <span dir="ltr" className="font-mono">
                seed-obs-reports.js
              </span>{" "}
              ثم رفع الملف من جديد — لكن الإصدارات المنشورة لا تعود.
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="obs-delete-confirm">
              اكتب رابط التقرير للتأكيد:{" "}
              <span dir="ltr" className="font-mono">
                {report.slug}
              </span>
            </Label>
            <Input
              id="obs-delete-confirm"
              dir="ltr"
              className="font-mono"
              value={typed}
              disabled={remove.isPending}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={report.slug}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            تبقى الملفات المرفوعة وصور البطاقة في مكتبة الوسائط ضمن{" "}
            <span dir="ltr" className="font-mono">
              OBS-reports/{report.slug}/
            </span>{" "}
            ويمكن حذفها يدويًا.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={remove.isPending}
          >
            إلغاء
          </Button>
          <Button
            variant="destructive"
            onClick={run}
            disabled={!confirmed || remove.isPending}
          >
            {remove.isPending ? <Loader /> : <Trash2 className="size-4" />}
            حذف نهائيًا
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
